import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import reportsService from "../services/reportsService";
import { Inbox, CheckCircle2, Layers, Timer, Gauge, Download, Printer } from "lucide-react";

// Monta um CSV multi-seção (separador ";", amigável p/ Excel BR) a partir dos
// dados já carregados e dispara o download. Sem dependência externa.
const buildReportCsv = (data, csat, tenant, from, to) => {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const row = (...cells) => cells.map(esc).join(";");
  const lines = [];
  const c = data?.cards || {};
  const frt = data?.frt || {};
  const fcr = data?.fcr || {};
  const sla = data?.sla || {};
  const cc = csat?.cards || {};

  lines.push(row("Relatório BenDesk", tenant || ""));
  lines.push(row("Período", from, to));
  lines.push("");
  lines.push(row("Indicador", "Valor"));
  lines.push(row("Criados no período", c.created ?? 0));
  lines.push(row("Encerrados no período", c.closed ?? 0));
  lines.push(row("Backlog (aberto agora)", c.backlog ?? 0));
  lines.push(row("MTTR (horas)", c.mttr_hours ?? 0));
  lines.push(row("SLA cumprido (%)", sla.compliance_pct ?? ""));
  lines.push(row("SLA dentro do prazo", sla.met ?? 0));
  lines.push(row("SLA violados (encerrados)", sla.breached ?? 0));
  lines.push(row("SLA abertos em risco", sla.open_em_risco ?? 0));
  lines.push(row("SLA abertos violados", sla.open_violado ?? 0));
  lines.push(row("1ª resposta média (h)", frt.avg_hours ?? ""));
  lines.push(row("Conformidade 1ª resposta (%)", frt.compliance_pct ?? ""));
  lines.push(row("Tempo médio entre interações (h)", frt.avg_gap_hours ?? ""));
  lines.push(row("FCR (%)", fcr.fcr_pct ?? ""));
  lines.push(row("Taxa de reabertura (%)", fcr.reopen_pct ?? ""));
  lines.push(row("Reabertos", fcr.reopened ?? 0));
  lines.push(row("CSAT geral (média)", cc.avg_general ?? ""));
  lines.push(row("CSAT analista (média)", cc.avg_analyst ?? ""));
  lines.push(row("CSAT taxa de resposta (%)", cc.response_pct ?? ""));
  lines.push("");
  lines.push(row("Entrada x Saída por dia"));
  lines.push(row("Data", "Criados", "Encerrados"));
  (data?.daily || []).forEach((d) => lines.push(row(d.label, d.created, d.closed)));
  lines.push("");
  lines.push(row("Por categoria"));
  lines.push(row("Categoria", "Qtd"));
  (data?.by_category || []).forEach((d) => lines.push(row(d.label, d.count)));
  lines.push("");
  lines.push(row("Por prioridade"));
  lines.push(row("Prioridade", "Qtd"));
  (data?.by_priority || []).forEach((d) => lines.push(row(d.label, d.count)));
  lines.push("");
  lines.push(row("Produtividade (encerrados por responsável)"));
  lines.push(row("Responsável", "Encerrados"));
  (data?.productivity || []).forEach((p) => lines.push(row(p.name, p.closed)));
  lines.push("");
  lines.push(row("Analistas mais bem avaliados (CSAT)"));
  lines.push(row("Analista", "Média", "Avaliações"));
  (csat?.analyst_ranking || []).forEach((a) => lines.push(row(a.name, a.avg, a.count)));

  // BOM p/ acentos no Excel.
  return "﻿" + lines.join("\n");
};

const downloadCsv = (content, filename) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

// Barras horizontais simples (label + valor + barra proporcional).
const HBars = ({ data, color = "from-blue-500 to-blue-700", empty = "Sem dados." }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  if (!data.length) return <p className="text-sm text-gray-500 py-4 text-center">{empty}</p>;
  return (
    <div className="space-y-2.5 pt-1">
      {data.map((d) => (
        <div key={d.label}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="truncate text-gray-700">{d.label}</span>
            <span className="font-semibold text-gray-900 ml-2">{d.count}</span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full rounded-full bg-gradient-to-r ${color}`} style={{ width: `${(d.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Estrelas (0..5) — preenche conforme arredondamento do valor.
const Stars = ({ value = 0, size = "text-lg" }) => {
  const v = Math.round(Number(value) || 0);
  return (
    <span className={`${size} tracking-tight`} aria-label={`${value} de 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= v ? "text-amber-500" : "text-gray-300"}>★</span>
      ))}
    </span>
  );
};

// Entrada × Saída por dia (barras agrupadas).
const DailyChart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => Math.max(d.created, d.closed)));
  if (!daily.length) return <p className="text-sm text-gray-500 py-6 text-center">Sem dados no período.</p>;
  return (
    <div className="overflow-x-auto">
      <div className="flex items-end gap-2 h-48 pt-2" style={{ minWidth: daily.length * 26 }}>
        {daily.map((d) => (
          <div key={d.date} className="flex h-full flex-col items-center gap-1" style={{ width: 22 }}>
            <div className="flex w-full flex-1 items-end justify-center gap-0.5">
              <div className="w-2 rounded-t bg-blue-500" style={{ height: `${Math.max(d.created ? 2 : 0, (d.created / max) * 100)}%` }} title={`${d.created} criados`} />
              <div className="w-2 rounded-t bg-emerald-500" style={{ height: `${Math.max(d.closed ? 2 : 0, (d.closed / max) * 100)}%` }} title={`${d.closed} encerrados`} />
            </div>
            <span className="text-[9px] text-gray-400 whitespace-nowrap">{d.label}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-500" /> Criados</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" /> Encerrados</span>
      </div>
    </div>
  );
};

const Relatorios = () => {
  const [from, setFrom] = useState(daysAgoISO(29));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [csat, setCsat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const activeTenantName = useMemo(() => localStorage.getItem("active_tenant_name") || "", []);

  const load = () => {
    setLoading(true);
    setError("");
    reportsService
      .overview(from, to)
      .then((d) => setData(d))
      .catch((e) => setError(e.response?.data?.detail || "Erro ao carregar relatórios."))
      .finally(() => setLoading(false));
    reportsService
      .csat(from, to)
      .then((d) => setCsat(d))
      .catch(() => setCsat(null));
  };

  useEffect(() => {
    load();
    // eslint pattern do projeto: sem exhaustive-deps
  }, [from, to]);

  const cards = data?.cards || {};
  const sla = data?.sla || {};
  const statCards = [
    { title: "Criados no período", value: cards.created ?? 0, icon: Inbox, color: "text-blue-600", bg: "bg-blue-100" },
    { title: "Encerrados no período", value: cards.closed ?? 0, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100" },
    { title: "Backlog (aberto agora)", value: cards.backlog ?? 0, icon: Layers, color: "text-amber-600", bg: "bg-amber-100" },
    { title: "MTTR (horas)", value: cards.mttr_hours ?? 0, icon: Timer, color: "text-violet-600", bg: "bg-violet-100" },
    { title: "SLA cumprido", value: cards.sla_compliance == null ? "—" : `${cards.sla_compliance}%`, icon: Gauge, color: "text-sky-600", bg: "bg-sky-100" },
  ];

  const aging = data?.aging || {};
  const agingBars = [
    { label: "≤ 24h", count: aging.le24h || 0 },
    { label: "> 24h", count: aging.gt24h || 0 },
    { label: "> 3 dias", count: aging.gt3d || 0 },
    { label: "> 7 dias", count: aging.gt7d || 0 },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
            <p className="text-gray-500">
              Visão operacional e gerencial{activeTenantName ? ` — ${activeTenantName}` : ""}.
            </p>
          </div>
          <div className="no-print flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">De</label>
              <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Até</label>
              <input type="date" value={to} min={from} max={todayISO()} onChange={(e) => setTo(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <Button variant="outline" onClick={() => downloadCsv(buildReportCsv(data, csat, activeTenantName, from, to), `relatorios_${from}_a_${to}.csv`)}
              disabled={loading || !data} className="h-auto gap-2 px-3 py-2 text-gray-700">
              <Download size={16} /> CSV
            </Button>
            <Button variant="outline" onClick={() => window.print()}
              disabled={loading} className="h-auto gap-2 px-3 py-2 text-gray-700">
              <Printer size={16} /> PDF
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {statCards.map((s) => {
                const Icon = s.icon;
                return (
                  <Card key={s.title}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-600 truncate">{s.title}</p>
                          <p className="text-3xl font-bold text-gray-900 mt-2">{s.value}</p>
                        </div>
                        <div className={`p-3 rounded-full ${s.bg} shrink-0`}>
                          <Icon className={`w-5 h-5 ${s.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card>
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Entrada × Saída (por dia)</h2>
                <DailyChart daily={data?.daily || []} />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card><CardContent className="pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Por categoria</h2>
                <HBars data={(data?.by_category || []).slice(0, 10)} />
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Por prioridade</h2>
                <HBars data={data?.by_priority || []} color="from-amber-500 to-amber-600" />
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Backlog por idade</h2>
                <HBars data={agingBars} color="from-rose-500 to-rose-600" empty="Sem chamados abertos." />
              </CardContent></Card>
              <Card><CardContent className="pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Produtividade (encerrados por responsável)</h2>
                <HBars
                  data={(data?.productivity || []).map((p) => ({ label: p.name, count: p.closed }))}
                  color="from-indigo-500 to-indigo-700"
                  empty="Nenhum chamado encerrado no período."
                />
              </CardContent></Card>
            </div>

            <Card><CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Tempo de 1ª resposta (FRT)</h2>
              <p className="text-sm text-gray-500 mb-4">Da abertura até a 1ª interação do agente (horário comercial). Conformidade vs meta de 1ª resposta do SLA.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div><p className="text-2xl font-bold text-gray-900">{(data?.frt?.avg_hours ?? null) == null ? "—" : `${data.frt.avg_hours}h`}</p><p className="text-xs text-gray-500">1ª resposta média</p></div>
                <div><p className="text-2xl font-bold text-sky-600">{(data?.frt?.compliance_pct ?? null) == null ? "—" : `${data.frt.compliance_pct}%`}</p><p className="text-xs text-gray-500">Conformidade 1ª resposta</p></div>
                <div><p className="text-2xl font-bold text-emerald-600">{data?.frt?.met || 0}<span className="text-base text-gray-400">/{data?.frt?.evaluated || 0}</span></p><p className="text-xs text-gray-500">Dentro da meta / avaliados</p></div>
                <div><p className="text-2xl font-bold text-violet-600">{(data?.frt?.avg_gap_hours ?? null) == null ? "—" : `${data.frt.avg_gap_hours}h`}</p><p className="text-xs text-gray-500">Tempo médio entre interações</p></div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Resolução na 1ª (FCR) e Reabertura</h2>
              <p className="text-sm text-gray-500 mb-4">Dos encerrados no período. FCR = encerrado sem nenhuma reabertura (Encerrado → reaberto).</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div><p className="text-2xl font-bold text-emerald-600">{(data?.fcr?.fcr_pct ?? null) == null ? "—" : `${data.fcr.fcr_pct}%`}</p><p className="text-xs text-gray-500">FCR (1ª resolução)</p></div>
                <div><p className="text-2xl font-bold text-rose-600">{(data?.fcr?.reopen_pct ?? null) == null ? "—" : `${data.fcr.reopen_pct}%`}</p><p className="text-xs text-gray-500">Taxa de reabertura</p></div>
                <div><p className="text-2xl font-bold text-gray-900">{data?.fcr?.resolved_first || 0}<span className="text-base text-gray-400">/{data?.fcr?.total_closed || 0}</span></p><p className="text-xs text-gray-500">Resolvidos de 1ª / encerrados</p></div>
                <div><p className="text-2xl font-bold text-amber-600">{data?.fcr?.reopened || 0}</p><p className="text-xs text-gray-500">Reabertos</p></div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">SLA (resolução, horário comercial)</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div><p className="text-2xl font-bold text-gray-900">{sla.compliance_pct == null ? "—" : `${sla.compliance_pct}%`}</p><p className="text-xs text-gray-500">Cumprimento</p></div>
                <div><p className="text-2xl font-bold text-emerald-600">{sla.met || 0}</p><p className="text-xs text-gray-500">Dentro do prazo</p></div>
                <div><p className="text-2xl font-bold text-red-600">{sla.breached || 0}</p><p className="text-xs text-gray-500">Violados (encerrados)</p></div>
                <div><p className="text-2xl font-bold text-amber-600">{sla.open_em_risco || 0}</p><p className="text-xs text-gray-500">Abertos em risco</p></div>
                <div><p className="text-2xl font-bold text-rose-600">{sla.open_violado || 0}</p><p className="text-xs text-gray-500">Abertos violados</p></div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Satisfação (CSAT)</h2>
              <p className="text-sm text-gray-500 mb-4">Pesquisa enviada no e-mail de encerramento (estrelas de 1 a 5).</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{csat?.cards?.avg_general == null ? "—" : csat.cards.avg_general}</p>
                  <Stars value={csat?.cards?.avg_general || 0} />
                  <p className="text-xs text-gray-500 mt-1">Satisfação geral{csat?.cards?.count_general ? ` (${csat.cards.count_general})` : ""}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{csat?.cards?.avg_analyst == null ? "—" : csat.cards.avg_analyst}</p>
                  <Stars value={csat?.cards?.avg_analyst || 0} />
                  <p className="text-xs text-gray-500 mt-1">Atendimento do analista{csat?.cards?.count_analyst ? ` (${csat.cards.count_analyst})` : ""}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-sky-600">{csat?.cards?.response_pct == null ? "—" : `${csat.cards.response_pct}%`}</p>
                  <p className="text-xs text-gray-500 mt-1">Taxa de resposta</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-600">{csat?.cards?.responded ?? 0}<span className="text-base text-gray-400">/{csat?.cards?.sent ?? 0}</span></p>
                  <p className="text-xs text-gray-500 mt-1">Respondidas / enviadas</p>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">Analistas mais bem avaliados</h3>
              {(csat?.analyst_ranking || []).length === 0 ? (
                <p className="text-sm text-gray-500 py-3 text-center">Sem avaliações de analista no período.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {csat.analyst_ranking.map((a, i) => (
                    <div key={a.name} className="flex items-center justify-between py-2">
                      <span className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">{i + 1}</span>
                        {a.name}
                      </span>
                      <span className="flex items-center gap-2">
                        <Stars value={a.avg} size="text-sm" />
                        <span className="text-sm font-semibold text-gray-900 w-8 text-right">{a.avg}</span>
                        <span className="text-xs text-gray-400 w-14 text-right">{a.count} aval.</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Relatorios;
